import FeedDetail from '../../../components/Feed/FeedDetail';
import type { IFeedItem } from '../../../components/FeedItem/FeedItem';
import { getReportDetail } from '../../../features/queries/getReportDetail';
import { entityId } from '../../../lib/ids';
import { useQ } from '../../../lib/query';
import { __ } from '../../../utils';
import { BottomSheet, Box, Skeleton, Stack } from '@presov.sk/presov-ui-react';
import React from 'react';

/**
 * Výška obsahu sheetu. Musí byť určitá, nie `100%`: `BottomSheet` má na ploche
 * len `max-height`, takže percento by sa nemalo o čo oprieť a `FeedDetail` by
 * stratil pripnutú hlavičku aj pätičku. Necháva sa pod `maxHeight` sheetu, aby
 * sa doň zmestil úchop aj odsadenie.
 */
const CONTENT_HEIGHT = '76vh';

type Props = {
  /** Krátke ID z adresy (`?detail=MQRJ73F`), nie appková podoba. */
  shortReportId: string;
  onClose: () => void;
};

/**
 * Detail podnetu vysunutý **nad zoznamom**, nie namiesto neho.
 *
 * Na mobile je odchod zo „Mojich podnetov" na mapu zbytočne ďaleko: občan chce
 * vidieť, čo mu úrad odpísal, a vrátiť sa do zoznamu. Preto sa tu detail
 * vysunie ako modálny sheet a adresa zostane na zozname (`?detail=`), takže ho
 * tlačidlo Späť zavrie. Na desktope taká cesta nie je - tam sa ide na mapu,
 * kde je pre panel miesto vedľa mapy ([[ReportDetailRedirect]]).
 *
 * Obsah je **ten istý** `FeedDetail`, aký kreslí panel nad mapou; líši sa len
 * tým, odkiaľ berie podnet. Panel ho má z feedu, tu sa doťahuje: v zozname
 * môže byť riadok na inej strane a odkaz sa dá otvoriť aj priamo.
 */
const ReportDetailSheet: React.FC<Props> = ({ shortReportId, onClose }) => {
  const reportId = entityId(shortReportId, 'report');

  const { data: report, isLoading } = useQ(getReportDetail, { reportId });

  const item = React.useMemo<IFeedItem | undefined>(() => {
    if (!report) return undefined;

    return {
      type: 'userReport',
      id: report.id,
      title: report.title,
      description: report.description ?? '',
      date: report.inserted_at,
      media: report.media.map((media) => ({
        url: media.public_url,
        type: media.media_type,
        filename: media.filename,
      })),
      location: report.location as {
        type: 'Point';
        coordinates: [number, number];
      },
      votesCount: report.votes_count ?? 0,
      status: report.status,
    };
  }, [report]);

  return (
    <BottomSheet open onClose={onClose} maxHeight='86%'>
      <Box sx={{ height: CONTENT_HEIGHT }}>
        {isLoading || !item ? (
          <Stack sx={{ height: 1, gap: 1.25 }}>
            <Skeleton variant='rounded' width='100%' height={132} />
            <Skeleton variant='rounded' width='60%' height={24} />
            <Skeleton variant='rounded' width='100%' height={72} />
          </Stack>
        ) : (
          // Geokóder tu nemá pohľad mapy - `reverseGeocode` si vtedy vyrobí
          // vlastný view model, takže adresa v riadku pod fotkami je aj tu.
          <FeedDetail
            compact
            item={item}
            onBack={onClose}
            backLabel={__('Späť na zoznam')}
          />
        )}
      </Box>
    </BottomSheet>
  );
};

export default ReportDetailSheet;
